import { Observation } from "@medplum/fhirtypes";

/**
 * Compares Medplum `meta.versionId` values (UUID v7) for newest-first ordering.
 * Lexicographic string order matches creation order for UUID7.
 */
export function compareMetaVersionIdDesc(
  firstVersionId: string | undefined,
  secondVersionId: string | undefined,
): number {
  return (secondVersionId ?? "").localeCompare(firstVersionId ?? "");
}

/**
 * Milliseconds since epoch from `meta.lastUpdated` or `issued`, for ordering Observation versions.
 */
export function getObservationRecencyMillis(observation: Observation): number {
  const isoTimestamp = observation.meta?.lastUpdated ?? observation.issued;
  if (!isoTimestamp) return 0;
  const millisecondsSinceEpoch = new Date(isoTimestamp).getTime();
  return Number.isNaN(millisecondsSinceEpoch) ? 0 : millisecondsSinceEpoch;
}

/**
 * Sort comparator for newest-first: higher recency (`meta.lastUpdated` / `issued`) sorts earlier;
 * tie-break with lexicographically greater `id`. Use with `Array.prototype.sort` for the same
 * winner rules as flowsheet Observation supersede and postpartum QR dedupe.
 */
export function compareObservationsByRecencyThenIdDesc(
  firstObservation: Observation,
  secondObservation: Observation,
): number {
  const recencyDifferenceMillis =
    getObservationRecencyMillis(secondObservation) - getObservationRecencyMillis(firstObservation);
  if (recencyDifferenceMillis !== 0) return recencyDifferenceMillis;
  return (secondObservation.id ?? "").localeCompare(firstObservation.id ?? "");
}

/**
 * Returns whichever observation is newer by {@link compareObservationsByRecencyThenIdDesc}.
 */
export function pickObservationNewerByRecencyThenId(
  firstObservation: Observation,
  secondObservation: Observation,
): Observation {
  return compareObservationsByRecencyThenIdDesc(firstObservation, secondObservation) > 0
    ? secondObservation
    : firstObservation;
}

/**
 * Sort comparator for newest-first with `meta.versionId` as the primary tie-break (then recency, then id).
 */
export function compareObservationsByVersionIdThenRecencyThenIdDesc(
  firstObservation: Observation,
  secondObservation: Observation,
): number {
  const versionDelta = compareMetaVersionIdDesc(
    firstObservation.meta?.versionId,
    secondObservation.meta?.versionId,
  );
  if (versionDelta !== 0) {
    return versionDelta;
  }
  return compareObservationsByRecencyThenIdDesc(firstObservation, secondObservation);
}

/**
 * Returns the observation that wins under {@link compareObservationsByVersionIdThenRecencyThenIdDesc}.
 */
export function pickObservationNewerByVersionIdThenRecencyThenId(
  firstObservation: Observation,
  secondObservation: Observation,
): Observation {
  return compareObservationsByVersionIdThenRecencyThenIdDesc(firstObservation, secondObservation) >
    0
    ? secondObservation
    : firstObservation;
}
